import {
  EditShipmentNoteInput,
  GetInvoiceForShipmentQuery,
  GetShipmentQuery,
  GetTripQuery,
  ShipmentNoteInput,
  Status,
  TransactionInput,
} from "../../../graphql/generated";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ShipmentDetails from "../ShipmentDetails";
import LoadingOverlay from "../../common/LoadingOverlay";
import {
  ShipmentChargeInputData,
  ShipmentDocumentInputData,
} from "../../../redux/slices/Types";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import {
  ContentCopy,
  DeleteOutlineOutlined,
  ModeEditOutlineOutlined,
  RestoreOutlined,
} from "@mui/icons-material";
import AssignmentModal from "../../trip/AssignmentModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentsListProps } from "../ShipmentForm/ShipmentDocumentsList/DocumentsList";
import {
  referenceNumberFieldLabels,
  referenceNumberFields,
} from "../ShipmentForm/ShipmentReferencesForm/ShipmentReferencesForm";
import LynksIconButton from "../../common/LynksIconButton";
import theme from "../../../theme";
import RoleGuard from "../../account/Access/RoleGuard";
import { useTranslation } from "react-i18next";
import IconSwitch from "../../common/IconSwitch";
import { testIds } from "./ShipmentInfos.testIds";

export function shouldShowShipmentAcceptButton(status: Status): boolean {
  return status === Status.Pending;
}

export function shouldShowShipmentAssignButton(status: Status): boolean {
  return status !== Status.Pending && status !== Status.InReview;
}

export const IN_REVIEW_POLL_INTERVAL_MS = 1500;
export const IN_REVIEW_POLL_MAX_MS = 30000;

export function shouldFetchShipmentDuringInReviewPoll(
  status: Status | undefined
): boolean {
  return status === undefined || status === Status.InReview;
}

export function shouldPollShipmentWhileInReview(
  status: Status | undefined,
  elapsedMs: number
): boolean {
  return status === Status.InReview && elapsedMs < IN_REVIEW_POLL_MAX_MS;
}

export function getInReviewRefetchInterval(
  status: Status | undefined,
  elapsedMs: number
): number | false {
  return shouldPollShipmentWhileInReview(status, elapsedMs)
    ? IN_REVIEW_POLL_INTERVAL_MS
    : false;
}

export function nextInReviewPollStartedAt(
  status: Status | undefined,
  currentStartedAt: number | null,
  now: number
): number | null {
  if (status === Status.InReview) {
    return currentStartedAt ?? now;
  }
  return null;
}

type ShipmentInfo = GetShipmentQuery["shipmentById"];
type InvoiceData = GetInvoiceForShipmentQuery["invoices"]["data"][0];

export type ShipmentDeletionOptions = {
  reason: string;
  isApplicableChargeOrderCancellation: boolean;
};

export type ShipmentInfosProps = {
  onDelete: (shipmentDeletionOptions: ShipmentDeletionOptions) => void;
  onRestore: () => void;
  loading: boolean;
  uploadingDocuments: boolean;
  error: unknown;
  shipment: ShipmentInfo | null | undefined;
  trip: GetTripQuery["tripById"] | null | undefined;
  invoice?: InvoiceData | null | undefined;
  invoiceLoading?: boolean;
  invoiceError?: unknown;
  refetchInvoice?: () => Promise<any>;
  onDocumentsUploaded: (documents: Array<ShipmentDocumentInputData>) => void;
  onNotesAdded: (notes: Array<ShipmentNoteInput>) => void;
  onNoteEdited: (notes: EditShipmentNoteInput) => void;
  onNoteDeleted: (id: string) => void;
  onDocumentDeleted: (id: string) => void;
  onDocumentsChanged: DocumentsListProps["onChange"];
  onChargeAdded: (charge: ShipmentChargeInputData) => void;
  onChargesChanged: (charges: ShipmentChargeInputData[]) => void;
  onRecalculate: () => void;
  recalculating: boolean;
  onChargeDeleted: (id: string) => void;
  onExpenseAdded: (expense: TransactionInput) => void;
  onExpensesChanged: (expenses: TransactionInput[]) => void;
  onExpenseDeleted: (id: string) => void;
  onRefresh: () => void;
  onShipmentLock: () => void;
  onShipmentUnlock: () => void;
  onAccept: () => void;
  onResendDocuments: (shipmentId: string) => void;
  resendingDocuments: boolean;
  canInvoice?: boolean;
  canAssign?: boolean;
  actionsInNewTab?: boolean;
};

export default function ShipmentInfos({
  onDelete,
  onRestore,
  loading,
  uploadingDocuments,
  resendingDocuments,
  error,
  shipment,
  trip,
  invoice,
  invoiceLoading,
  invoiceError,
  refetchInvoice,
  onDocumentsUploaded,
  onNotesAdded,
  onNoteEdited,
  onNoteDeleted,
  onDocumentDeleted,
  onDocumentsChanged,
  onResendDocuments,
  onChargeAdded,
  onChargeDeleted,
  onChargesChanged,
  onRecalculate,
  recalculating,
  onExpenseAdded,
  onExpensesChanged,
  onExpenseDeleted,
  onRefresh,
  canInvoice,
  canAssign = true,
  actionsInNewTab,
  onShipmentLock,
  onShipmentUnlock,
  onAccept,
}: ShipmentInfosProps) {
  const [isAssignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [shipmentDeletionOptions, setShipmentDeletionOptions] = useState<{
    reason: string;
    isApplicableChargeOrderCancellation: boolean;
  }>({
    reason: "",
    isApplicableChargeOrderCancellation: false,
  });
  const navigate = useNavigate();
  const { t } = useTranslation(["orders", "trips"]);

  const handleShipmentDeletionReasonChange = useCallback(
    (reason: string) => {
      setShipmentDeletionOptions((prev) =>
        prev.reason === reason ? prev : { ...prev, reason }
      );
    },
    []
  );

  const handleShipmentCancellationChargeToggle = useCallback(
    (isApplicableChargeOrderCancellation: boolean) => {
      setShipmentDeletionOptions((prev) =>
        prev.isApplicableChargeOrderCancellation === isApplicableChargeOrderCancellation
          ? prev
          : { ...prev, isApplicableChargeOrderCancellation }
      );
    },
    []
  );

  if (loading) {
    return (
      <Box
        sx={{
          position: "relative",
          height: "100%",
        }}
      >
        <LoadingOverlay loading={loading} />
      </Box>
    );
  }
  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (shipment) {
    return (
      <Container>
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          sx={{
            mb: 3,
          }}
          justifyContent={{
            xs: "center",
            sm: "flex-end",
          }}
          alignItems="center"
        >
          {shipment.status !== Status.Deleted ? (
            <>
              <Stack
                direction="row"
                spacing={2}
                sx={{ mr: 1 }}
                mb={{
                  xs: 2,
                  sm: 0,
                }}
                width={{
                  xs: "100%",
                  sm: "auto",
                }}
                justifyContent={{
                  xs: "center",
                  sm: "flex-end",
                }}
              >
                <RoleGuard roles={["Carrier Admin", "Manager", "Dispatcher"]}>
                  {shipment.status === Status.Forecasted ? (
                    <Button data-testid={testIds.shipmentInfosLock}
                      onClick={() => {
                        onShipmentLock();
                      }}
                      variant="outlined"
                    >
                      Lock Order
                    </Button>
                  ) : shipment.status === Status.ForecastedLocked ? (
                    <Button data-testid={testIds.shipmentInfosUnlock}
                      onClick={() => {
                        onShipmentUnlock();
                      }}
                      variant="outlined"
                    >
                      Unlock Order
                    </Button>
                  ) : null}
                  {shouldShowShipmentAcceptButton(shipment.status) ? (
                    <Button data-testid={testIds.shipmentInfosAccept}
                      onClick={() => {
                        onAccept();
                      }}
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      Accept
                    </Button>
                  ) : null}
                  {!shipment.isSplit &&
                  !shipment.dailyRotationCount &&
                  !shipment.recurrence &&
                  canAssign &&
                  shouldShowShipmentAssignButton(shipment.status) ? (
                    <Button data-testid={testIds.shipmentInfosAssign}
                      onClick={() => {
                        setAssignmentModalOpen(true);
                      }}
                      variant="contained"
                      color="lightPrimary"
                      size="large"
                      id="assign-button"
                    >
                      {t("trips:assign", "Assign")}
                    </Button>
                  ) : null}
                </RoleGuard>
              </Stack>
              <RoleGuard roles={["Carrier Admin", "Manager", "Dispatcher"]}>
                <LynksIconButton
                  sx={{
                    color: "black",
                    backgroundColor: theme.palette.primaryBackground.main,
                  }}
                  color="primary"
                  href={`/orders/edit/${shipment?._id}`}
                  target={actionsInNewTab ? "_blank" : undefined}
                >
                  <ModeEditOutlineOutlined />
                </LynksIconButton>
                {!shipment.isFromSplit ? (
                  <>
                    <LynksIconButton
                      sx={{
                        color: "black",
                        backgroundColor: theme.palette.primaryBackground.main,
                      }}
                      color="primary"
                      href={`/orders/new?duplicatedShipmentId=${shipment._id}`}
                      target={actionsInNewTab ? "_blank" : undefined}
                    >
                      <ContentCopy />
                    </LynksIconButton>
                    <LynksIconButton
                      sx={{
                        color: "black",
                        backgroundColor: theme.palette.primaryBackground.main,
                      }}
                      onClick={() => setIsCancelModalVisible(true)}
                      color="error"
                    >
                      <DeleteOutlineOutlined />
                    </LynksIconButton>
                  </>
                ) : null}
              </RoleGuard>
            </>
          ) : (
            <LynksIconButton
              sx={{
                color: "black",
                backgroundColor: theme.palette.primaryBackground.main,
              }}
              onClick={() => onRestore()}
            >
              <RestoreOutlined />
            </LynksIconButton>
          )}
        </Stack>
        {shipment.status === Status.Deleted ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cancellation reason: {shipment.reasonForCancellation || "N/A"}
          </Alert>
        ) : null}
        {shipment.recurrence ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is a recurrent order template. Individual occurrences are managed as separate orders.
          </Alert>
        ) : null}
        {shipment.isApplicableChargeOrderCancellation ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Truck ordered not used charge(s) is applied
          </Alert>
        ) : null}
        <ShipmentDetails
          uploadingDocuments={uploadingDocuments}
          resendingDocuments={resendingDocuments}
          onResendDocuments={onResendDocuments}
          onDocumentDeleted={onDocumentDeleted}
          onNoteEdited={onNoteEdited}
          onNoteDeleted={onNoteDeleted}
          shipment={shipment}
          invoice={invoice}
          invoiceLoading={invoiceLoading}
          invoiceError={invoiceError}
          refetchInvoice={refetchInvoice}
          onDocumentsUploaded={onDocumentsUploaded}
          onDocumentsChanged={onDocumentsChanged}
          onNotesAdded={onNotesAdded}
          onChargeAdded={onChargeAdded}
          onChargesChanged={onChargesChanged}
          onRecalculate={onRecalculate}
          recalculating={recalculating}
          onChargeDeleted={onChargeDeleted}
          onExpenseAdded={onExpenseAdded}
          onExpensesChanged={onExpensesChanged}
          onExpenseDeleted={onExpenseDeleted}
          onRefresh={onRefresh}
          trip={trip}
          isInputView={false}
          canInvoice={canInvoice}
        />

        <AssignmentModal
          tripId={shipment.tripId}
          shipments={[shipment]}
          open={isAssignmentModalOpen}
          onClose={(tripId) => {
            setAssignmentModalOpen(false);
            if (tripId) {
              navigate(`/trips/details/${tripId}`);
            }
          }}
        />

        <Dialog
          open={isCancelModalVisible}
          onClose={() => setIsCancelModalVisible(false)}
          aria-labelledby="dialog-confirmation"
          aria-describedby="dialog-confirmation"
        >
          <DialogTitle
            sx={{
              color: "error.main",
            }}
            id="dialog-confirmation"
          >
            Danger zone
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography>
                {`Do you really want to cancel this shipment? This action cannot be undone.`}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Typography>Shipment #:&nbsp;</Typography>
                <Typography fontWeight={"bold"}>
                  {shipment?.shipmentNumber}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Typography>Customer/Broker:&nbsp;</Typography>
                <Typography></Typography>
                <Typography fontWeight={"bold"}>
                  {shipment?.customer.name}
                </Typography>
              </Box>
              {referenceNumberFields.map((field) => (
                <Box key={field} sx={{ display: "flex", flexDirection: "row" }}>
                  <Typography>
                    {referenceNumberFieldLabels[field]}:&nbsp;
                  </Typography>
                  <Typography fontWeight={"bold"}>
                    {shipment[field]?.join(", ")}
                  </Typography>
                </Box>
              ))}
              <ReasonForm
                reasons={cancellationReasons}
                onChange={handleShipmentDeletionReasonChange}
              />
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <IconSwitch
                      checked={
                        !!shipmentDeletionOptions.isApplicableChargeOrderCancellation
                      }
                      inputProps={{
                        "data-testid":
                          testIds.shipmentInfosOrderCancellationCharge,
                      } as React.InputHTMLAttributes<HTMLInputElement>}
                      onChange={(e) => {
                        handleShipmentCancellationChargeToggle(
                          e.target.checked
                        );
                      }}
                      onIcon="check"
                      offIcon="close"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "success.main",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "success.main",
                          },
                      }}
                    />
                  }
                  label={t(
                    "orders:isOrderCancellationApplicable",
                    "Apply truck ordered not used charge(s)"
                  )}
                />
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button data-testid={testIds.shipmentInfosKeepOrder}
              color="primary"
              onClick={() => setIsCancelModalVisible(false)}
            >
              No
            </Button>
            <Button data-testid={testIds.shipmentInfosConfirmCancellation}
              disabled={
                !shipmentDeletionOptions.reason ||
                shipmentDeletionOptions.reason.trim() === ""
              }
              color="error"
              onClick={() => {
                onDelete(shipmentDeletionOptions);
                setIsCancelModalVisible(false);
              }}
            >
              Yes, cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  } else {
    return <div></div>;
  }
}

const cancellationReasons = [
  {
    label: "Request from management",
    value: "request",
  },
  {
    label: "Required documents non-fulfilled",
    value: "non-fulfilled",
  },
  {
    label: "Other",
    value: "other",
  },
];

export const ReasonForm = ({
  reasons,
  title,
  onChange,
}: {
  reasons: Array<{ label: string; value: string }>;
  title?: string;
  onChange: (message: string) => void;
}) => {
  const [reason, setReason] = useState<string>(reasons[0].label);
  const [choice, setChoice] = useState<string>(reasons[0].value);
  const [otherReason, setOtherReason] = useState<string>("");
  const lastEmittedReason = useRef<string>(reasons[0].label);

  useEffect(() => {
    const nextReason = reason === "Other" ? otherReason : reason;
    if (nextReason !== lastEmittedReason.current) {
      lastEmittedReason.current = nextReason;
      onChange(nextReason);
    }
  }, [reason, otherReason, onChange]);

  return (
    <FormControl fullWidth component="fieldset">
      <FormLabel component="legend">{title || "Cancellation reason"}</FormLabel>
      <RadioGroup data-testid={testIds.shipmentInfosReasonOptions}
        aria-label="reason"
        name="reason"
        onChange={(e) => {
          setChoice(e.target.value);
          setReason(
            reasons.find((r) => r.value === e.target.value)?.label || ""
          );
          setOtherReason("");
        }}
        value={choice || ""}
      >
        {reasons.map((reason) => (
          <FormControlLabel
            value={reason.value}
            control={<Radio inputProps={{ "data-testid": `${testIds.shipmentInfosReasonOption}-${reason.value}` } as React.InputHTMLAttributes<HTMLInputElement>} />}
            label={reason.label}
          />
        ))}
      </RadioGroup>
      {choice === "other" && (
        <TextField
          inputProps={{ "data-testid": testIds.shipmentInfosReasonField } as React.InputHTMLAttributes<HTMLInputElement>}
          onChange={(e) => setOtherReason(e.target.value)}
          fullWidth
          multiline
          rows={4}
          label="Reason"
          value={otherReason}
        />
      )}
    </FormControl>
  );
};
